import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTarget, FiTrendingUp, FiAlertTriangle, FiCheckCircle, FiClock, FiDollarSign, FiTrendingDown, FiActivity, FiCalculator, FiEdit2 } = FiIcons;

const GoalAnalysis = ({ goal, years, currentCost, inflationRate, timeBucket, recommendedCategories, expectedReturns, userProfile }) => {
  const [showLumpsum, setShowLumpsum] = useState(false);
  const [customReturn, setCustomReturn] = useState(null);
  const [isEditingReturn, setIsEditingReturn] = useState(false);

  // Use custom return if set, otherwise use expected return from goal input
  const effectiveReturn = customReturn !== null ? customReturn : parseFloat(goal.expectedReturn);

  // --- 1. Basic Calculations ---
  // Ensure years is at least 1 to avoid division by zero in some formulas
  const safeYears = Math.max(years, 0.1); 
  const futureValue = currentCost * Math.pow(1 + inflationRate / 100, safeYears);
  const isShortTerm = safeYears <= 3;

  // SIP Calculation Helper
  const calculateSIP = (annualReturn, targetAmount, durationYears) => {
    if (durationYears <= 0) return targetAmount;
    const monthlyReturn = annualReturn / 100 / 12;
    const months = durationYears * 12;
    if (monthlyReturn === 0) return targetAmount / months;
    const sip = (targetAmount * monthlyReturn) / (Math.pow(1 + monthlyReturn, months) - 1);
    return isFinite(sip) ? sip : 0;
  };

  // Lumpsum Calculation Helper
  const calculateLumpsum = (annualReturn, targetAmount, durationYears) => {
    const annualRate = annualReturn / 100;
    if (annualRate === 0) return targetAmount;
    const lumpsum = targetAmount / Math.pow(1 + annualRate, durationYears);
    return isFinite(lumpsum) ? lumpsum : 0;
  };

  // --- 2. Right vs Wrong Choice Logic ---
  let rightChoice = {};
  let wrongChoice = {};

  if (isShortTerm) {
    // Short Term Logic (< 3 Years)
    // Right: Debt/Liquid (Safety)
    // Wrong: Equity (High Volatility - use >12% return assumption for calculation but highlight risk)
    rightChoice = {
      type: "Debt / Liquid Funds",
      returnRate: effectiveReturn,
      sip: calculateSIP(effectiveReturn, futureValue, safeYears),
      lumpsum: calculateLumpsum(effectiveReturn, futureValue, safeYears),
      riskLabel: "Low",
      desc: "Capital protection is priority."
    };
    wrongChoice = {
      type: "Wrong Category",
      returnRate: 12, // Fixed at 12% for short-term wrong category assumption
      sip: calculateSIP(12, futureValue, safeYears),
      lumpsum: calculateLumpsum(12, futureValue, safeYears),
      riskLabel: "Very High",
      desc: "High volatility risk."
    };
  } else {
    // Long Term Logic (> 3 Years)
    // Right: Equity (Growth)
    // Wrong: Low return category (4% less)
    rightChoice = {
      type: "Equity / Hybrid",
      returnRate: effectiveReturn,
      sip: calculateSIP(effectiveReturn, futureValue, safeYears),
      lumpsum: calculateLumpsum(effectiveReturn, futureValue, safeYears),
      riskLabel: "Moderate",
      desc: "Growth to beat inflation."
    };
    wrongChoice = {
      type: "Wrong Category",
      returnRate: Math.max(effectiveReturn - 4, 1), // 4% less, minimum 1%
      sip: calculateSIP(Math.max(effectiveReturn - 4, 1), futureValue, safeYears),
      lumpsum: calculateLumpsum(Math.max(effectiveReturn - 4, 1), futureValue, safeYears),
      riskLabel: "Low (Returns)",
      desc: "Returns may not beat inflation."
    };
  }

  // --- 3. Impact Calculations ---
  const calculateDelay = (targetCorpus, monthlyInvestment, actualReturnRate) => {
    const r = actualReturnRate / 100 / 12;
    const pmt = -monthlyInvestment;
    const fv = targetCorpus;
    
    // Safety check for log arguments
    const logArg = (fv * r) / (-pmt) + 1;
    if (logArg <= 0) return 99; // Return a large delay if impossible

    const months = Math.log(logArg) / Math.log(1 + r);
    return isFinite(months) ? months / 12 : 99;
  };

  let impactData = {};

  if (!isShortTerm) {
    const extraSIP = wrongChoice.sip - rightChoice.sip;
    const totalExtraCost = extraSIP * safeYears * 12;
    const yearsWithWrongRate = calculateDelay(futureValue, rightChoice.sip, wrongChoice.returnRate);
    const delayYears = Math.max(yearsWithWrongRate - safeYears, 0);

    impactData = {
      title: "Impact of Low Returns (Wrong Category)",
      delay: delayYears,
      extraMonthly: extraSIP,
      totalExtra: totalExtraCost,
      message: `Choosing a low-return fund for a long-term goal forces you to invest significantly more.`,
      extraLumpsum: wrongChoice.lumpsum - rightChoice.lumpsum
    };
  } else {
    // Short term impact
    const potentialCrashLoss = futureValue * 0.15;
    impactData = {
      title: "Impact of Market Volatility (Wrong Category)",
      potentialLoss: potentialCrashLoss,
      message: `Choosing volatile equity funds for short-term goals exposes you to market crashes right when you need the money.`,
      higherLumpsum: wrongChoice.lumpsum - rightChoice.lumpsum
    };
  }

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
      {/* Goal Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start">
          <div className="flex items-center space-x-3 mb-4 sm:mb-0">
            <SafeIcon icon={FiTarget} className="text-2xl text-yellow-300" />
            <div>
              <h2 className="text-2xl font-bold">{goal.name}</h2>
              <p className="text-indigo-100 text-sm">Target: {years} Years | {timeBucket}</p>
            </div>
          </div>
          <div className="flex space-x-8 text-right w-full sm:w-auto justify-end">
            <div className="hidden sm:block">
              <div className="text-xs text-indigo-200 uppercase tracking-wider font-semibold">Present Cost</div>
              <div className="text-xl font-bold">₹{Math.round(currentCost).toLocaleString('en-IN')}</div>
            </div>
            <div>
              <div className="text-xs text-indigo-200 uppercase tracking-wider font-semibold">Future Cost</div>
              <div className="text-2xl font-bold">₹{Math.round(futureValue).toLocaleString('en-IN')}</div>
              <div className="text-xs text-indigo-300">Inflation: {inflationRate}%</div>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        {/* Toggle Buttons */}
        <div className="flex items-center space-x-4 mb-6 border-b border-gray-200 pb-4">
          <button
            onClick={() => setShowLumpsum(false)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${!showLumpsum ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <SafeIcon icon={FiTrendingUp} className="mr-2" />
            SIP Analysis
          </button>
          <button
            onClick={() => setShowLumpsum(true)}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${showLumpsum ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <SafeIcon icon={FiCalculator} className="mr-2" />
            Lumpsum Analysis
          </button>
        </div>

        {/* Custom Return Input */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-blue-900 flex items-center">
              <SafeIcon icon={FiEdit2} className="mr-2" />
              Expected Return Rate
            </h4>
            {!isEditingReturn && (
              <button
                onClick={() => setIsEditingReturn(true)}
                className="text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Edit Return
              </button>
            )}
          </div>
          {isEditingReturn ? (
            <div className="flex items-center space-x-3">
              <input
                type="number"
                step="0.1"
                value={customReturn !== null ? customReturn : effectiveReturn}
                onChange={(e) => setCustomReturn(parseFloat(e.target.value))}
                className="w-32 px-3 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="12.0"
              />
              <span className="text-blue-900 font-medium">%</span>
              <button
                onClick={() => setIsEditingReturn(false)}
                className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 transition-colors text-sm"
              >
                Apply
              </button>
              <button
                onClick={() => { setCustomReturn(null); setIsEditingReturn(false); }}
                className="px-3 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors text-sm"
              >
                Reset
              </button>
            </div>
          ) : (
            <div className="text-blue-900">
              <span className="text-2xl font-bold">{effectiveReturn}%</span>
              <span className="text-sm text-blue-700 ml-2">
                {customReturn !== null ? '(Custom)' : '(From Goal Input)'}
              </span>
            </div>
          )}
        </div>

        {/* Comparison Section */}
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <SafeIcon icon={FiActivity} className="mr-2 text-indigo-600" />
          {showLumpsum ? 'Lumpsum Investment Comparison' : 'SIP Investment Comparison'}
        </h3>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* RIGHT CHOICE */}
          <div className="bg-green-50 rounded-xl p-5 border border-green-200 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
              RECOMMENDED
            </div>
            <div className="flex items-center space-x-2 mb-3">
              <SafeIcon icon={FiCheckCircle} className="text-green-600 text-xl" />
              <h4 className="font-bold text-green-900">{rightChoice.type}</h4>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-green-800">
                <span>Expected Return:</span>
                <span className="font-bold">{rightChoice.returnRate}%</span>
              </div>
              <div className="flex justify-between text-green-800">
                <span>Risk Level:</span>
                <span className="font-bold">{rightChoice.riskLabel}</span>
              </div>
              <div className="mt-4 pt-3 border-t border-green-200">
                <div className="text-green-700 text-xs uppercase font-semibold">
                  Required {showLumpsum ? 'One-Time' : 'Monthly'} Investment
                </div>
                <div className="text-2xl font-bold text-green-900">
                  ₹{Math.round(showLumpsum ? rightChoice.lumpsum : rightChoice.sip).toLocaleString('en-IN')}
                </div>
                <div className="text-xs text-green-700 mt-1">
                  {showLumpsum ? 'today' : 'per month'}
                </div>
              </div>
            </div>
          </div>

          {/* WRONG CHOICE */}
          <div className="bg-red-50 rounded-xl p-5 border border-red-200 relative overflow-hidden">
            <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
              AVOID
            </div>
            <div className="flex items-center space-x-2 mb-3">
              <SafeIcon icon={FiAlertTriangle} className="text-red-600 text-xl" />
              <h4 className="font-bold text-red-900">{wrongChoice.type}</h4>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-red-800">
                <span>Expected Return:</span>
                <span className="font-bold">{wrongChoice.returnRate}%</span>
              </div>
              <div className="flex justify-between text-red-800">
                <span>Risk Level:</span>
                <span className="font-bold">{wrongChoice.riskLabel}</span>
              </div>
              <div className="mt-4 pt-3 border-t border-red-200">
                <div className="text-red-700 text-xs uppercase font-semibold">
                  Required {showLumpsum ? 'One-Time' : 'Monthly'} Investment
                </div>
                <div className="text-2xl font-bold text-red-900">
                  ₹{Math.round(showLumpsum ? wrongChoice.lumpsum : wrongChoice.sip).toLocaleString('en-IN')}
                </div>
                <div className="text-xs text-red-700 mt-1">
                  {showLumpsum ? 'today' : 'per month'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* IMPACT ANALYSIS SECTION */}
        <div className={`rounded-xl p-6 border-l-4 ${isShortTerm ? 'bg-orange-50 border-orange-500' : 'bg-yellow-50 border-yellow-500'}`}>
          <div className="flex items-start space-x-4">
            <SafeIcon icon={isShortTerm ? FiTrendingDown : FiClock} className={`text-3xl ${isShortTerm ? 'text-orange-600' : 'text-yellow-600'}`} />
            <div className="flex-1">
              <h4 className={`text-lg font-bold mb-2 ${isShortTerm ? 'text-orange-900' : 'text-yellow-900'}`}>
                {impactData.title}
              </h4>
              <p className={`text-sm mb-4 ${isShortTerm ? 'text-orange-800' : 'text-yellow-800'}`}>
                {impactData.message}
              </p>

              {/* Dynamic Impact Metrics */}
              {!isShortTerm ? (
                // Long Term Impact Display
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/50 rounded-lg p-3">
                    <div className="text-xs text-yellow-800 font-semibold uppercase">Potential Delay</div>
                    <div className="text-xl font-bold text-yellow-900">
                      +{Math.round(impactData.delay * 10) / 10} Years
                    </div>
                    <div className="text-xs text-yellow-700">
                      if investing same {showLumpsum ? 'lumpsum' : 'SIP'} amount
                    </div>
                  </div>
                  <div className="bg-white/50 rounded-lg p-3">
                    <div className="text-xs text-yellow-800 font-semibold uppercase">
                      Extra {showLumpsum ? 'Lumpsum' : 'SIP'} Needed
                    </div>
                    <div className="text-xl font-bold text-yellow-900">
                      +₹{Math.round(showLumpsum ? impactData.extraLumpsum : impactData.extraMonthly).toLocaleString('en-IN')}
                    </div>
                    <div className="text-xs text-yellow-700">
                      {showLumpsum ? 'additional one-time investment' : 'per month to catch up'}
                    </div>
                  </div>
                </div>
              ) : (
                // Short Term Impact Display
                <div className="bg-white/50 rounded-lg p-4 border border-orange-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-orange-900 font-bold">Scenario: 15% Market Correction</span>
                    <span className="bg-orange-200 text-orange-800 text-xs px-2 py-1 rounded">Risk Alert</span>
                  </div>
                  <div className="text-sm text-orange-800">
                    If the market drops just before you need the money, you could lose:
                  </div>
                  <div className="text-2xl font-bold text-red-600 mt-1">
                    ~ ₹{Math.round(impactData.potentialLoss).toLocaleString('en-IN')}
                  </div>
                  <div className="text-xs text-orange-700 mt-1">
                    Equity is too volatile for {years} year goals.
                  </div>
                  {showLumpsum && (
                    <div className="mt-3 pt-3 border-t border-orange-200">
                      <div className="text-xs text-orange-800">
                        Higher return assumption requires:
                      </div>
                      <div className="text-lg font-bold text-orange-900">
                        ₹{Math.round(impactData.higherLumpsum).toLocaleString('en-IN')} less lumpsum
                      </div>
                      <div className="text-xs text-orange-700">
                        But comes with significant volatility risk
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Current Status Check */}
        {goal.currentlyInvesting && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Currently investing <span className="font-semibold text-gray-900">₹{goal.currentSIP}/mo</span>
              </div>
              <div>
                {parseInt(goal.currentSIP) < rightChoice.sip ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Gap: ₹{Math.round(rightChoice.sip - parseInt(goal.currentSIP)).toLocaleString('en-IN')}
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    On Track
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoalAnalysis;